import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import type { ticketType } from '@nsc/otrs/server/client.js';
import type { fieldType } from '../field-type.js';

export const OTRS_FIELD_MAPPING = {
  short_description: 'Title',
  site_id: 'SiteCode',
  site_street_address: 'Address',
  site_city: 'City',
  site_province_state: 'StateProvince',
  site_country: 'Country',
  site_zip_code: 'ZipCode',
  site_type: 'SiteName',
  caller: 'ContactName',
  caller_email: 'ContactEmail',
  caller_phone: 'ContactPhone',
  number: 'CustomerTicketReference',
  part_number: 'Reference',
};

export const NON_DYNAMIC_FIELDS = [
  'Priority',
  'State',
  'Queue',
  'OwnerID',
  'ResponsibleID',
  'CustomerUser',
  'Title',
  'Type',
  'Service',
];

export const READABLE_FIELD_MAPPING = Object.fromEntries(
  Object.entries(OTRS_FIELD_MAPPING).map(([key, value]) => [value, key]),
);

@Entity()
export class FieldExtractionKV extends AdornisEntity {
  static override _class = 'FieldExtractionKV';

  static override resolveID(entity: any): string {
    return A.getGloballyUniqueID();
  }

  @validate(nonOptional())
  @Field(() => String)
  key: Maybe<string>;

  @Field(() => String)
  value: Maybe<string>;
}

@Entity()
export class FieldExtractionResult extends AdornisEntity {
  static override _class = 'FieldExtractionResult';

  static override resolveID(entity: any): string {
    const str = entity.input ?? '';
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return `${hash.toFixed(0)}_${entity.createdAt}`;
  }

  @validate(nonOptional())
  @Field(() => String)
  input: Maybe<string>;

  @Field(() => Date, { default: v => v ?? new Date() })
  createdAt: Maybe<Date>;

  @Field(() => [FieldExtractionKV])
  values: Maybe<FieldExtractionKV[]>;

  @Field(() => [String])
  missing: Maybe<string[]>;

  @Field(() => [String])
  invalid: Maybe<string[]>;

  valuesEnsureMissing(fields: fieldType[]) {
    for (const key of this.missingOnlyRequired(fields) ?? []) {
      if (this.values?.find(v => v.key === key)) continue;
      this.values?.push(
        new FieldExtractionKV({
          key,
          value: '',
        }),
      );
    }
    return this.values ?? [];
  }

  toOTRS(fields: fieldType[]) {
    const res: Record<string, string> = {};
    for (const kv of this.values ?? []) {
      if (!kv.key || !kv.value) continue;
      res[OTRS_FIELD_MAPPING[kv.key] ?? kv.key] = kv.value;
    }
    this.invalidToOTRS(fields).forEach(key => {
      delete res[key];
    });
    this.missingToOTRS(fields).forEach(key => {
      delete res[key];
    });
    const allFieldsMapped = Object.entries(res)
      .filter(([_, value]) => value !== 'null')
      .filter(([key]) => fields.map(field => field.key).includes(key))
      .map(([key, value]) => ({ Name: key, Value: value }));
    return {
      ...Object.fromEntries(
        allFieldsMapped.filter(pair => NON_DYNAMIC_FIELDS.includes(pair.Name)).map(pair => [pair.Name, pair.Value]),
      ),
      DynamicField: allFieldsMapped.filter(pair => !NON_DYNAMIC_FIELDS.includes(pair.Name)),
    };
  }

  /**
   * converts field to otrs fields but fills only the ones that are changed from the current ticket
   *
   * @param noOverride if true will not override fields that are already set
   */
  toOTRSOnlyChanged(fields: fieldType[], otrsTicket: ticketType, noOverride: boolean = false) {
    const otrsFields = this.toOTRS(fields);

    const changedTopLevelFields = Object.fromEntries(
      Object.entries(otrsFields)
        .filter(([key]) => !['DynamicField'].includes(key))
        .filter(([key, value]) => otrsTicket[key] !== value && (!noOverride || !otrsTicket[key])),
    );

    const changedDynamicFields = otrsFields.DynamicField?.filter(dF => {
      const dynamicFieldValue = otrsTicket.DynamicField?.find(dF2 => dF2.Name === dF.Name)?.Value;
      return dynamicFieldValue !== dF.Value && (!noOverride || !dynamicFieldValue);
    });

    return {
      ...changedTopLevelFields,
      DynamicField: changedDynamicFields,
    };
  }

  missingToOTRS(fields: fieldType[]) {
    return (
      this.missing
        ?.filter(m =>
          fields
            .filter(field => field.required)
            .map(field => field.key)
            .includes(m),
        )
        .map(m => OTRS_FIELD_MAPPING[m] ?? m) ?? []
    );
  }

  invalidToOTRS(fields: fieldType[]) {
    return (
      this.invalid
        ?.filter(m =>
          fields
            .filter(field => field.required)
            .map(field => field.key)
            .includes(m),
        )
        .map(m => OTRS_FIELD_MAPPING[m] ?? m) ?? []
    );
  }

  findValue(key: string) {
    return this.values?.find(v => v.key === key);
  }

  isMissing(key: string) {
    return this.missing?.includes(key);
  }

  isInvalid(key: string) {
    return this.invalid?.includes(key);
  }

  missingOnlyRequired(fields: fieldType[]) {
    return (
      this.missing?.filter(m =>
        fields
          .filter(field => field.required)
          .map(field => field.key)
          .includes(m),
      ) ?? []
    );
  }

  invalidOnlyRequired(fields: fieldType[]) {
    return (
      this.invalid?.filter(m =>
        fields
          .filter(field => field.required)
          .map(field => field.key)
          .includes(m),
      ) ?? []
    );
  }

  addInvalid(fields: fieldType[]) {
    this.invalid = fields
      .filter(field => field.validator)
      .filter(field => !field.validator!(this.findValue(field.key)?.value, this))
      .map(field => field.key);

    // TODO: OTRS API accepts it even if it's not valid, so let's check again if this is really needed
    //
    // const siteProvinceState = this.findValue('site_province_state');
    // if (
    //   !this.missing?.includes('site_province_state') &&
    //   siteProvinceState &&
    //   (siteProvinceState.value?.length ?? 0) > 0 &&
    //   siteProvinceState.value?.length !== 2
    // ) {
    //   this.invalid?.push('site_province_state');
    // }
  }
}
